import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  HairlineCard,
  Icons,
  iconSize,
  Stack,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  isConfiguredBylawCategory,
  taxonomyLabel,
  type ComplaintSource,
  type FileDetail,
} from '@munifocus/contracts-gov';
import { StageLayout } from '../../components/StageLayout';
import { countyLabel, withSpecify } from '../../lib/file-display';
import { useAdvanceStage, useComplaint } from '../../lib/files-api';
import { useAdvanceWorkflow } from '../../lib/bylaw-workflows-api';
import { useIntakeMethods, usePropertyTypes } from '../../lib/taxonomies-api';
import { frenchFirstOfMonth } from '@munifocus/utils';

const SOURCE_LABEL_KEY: Record<ComplaintSource, string> = {
  citizen: 'gov.file.sourceCitizen',
  internal: 'gov.file.sourceInternal',
};

const BYLAW_LABEL_KEY: Record<FileDetail['bylawCategory'], string> = {
  dangerous_unsightly: 'gov.bylaw.dangerous_unsightly',
  parking: 'gov.bylaw.parking',
  other: 'gov.bylaw.other',
  property_standards: 'gov.bylaw.property_standards',
  zoning: 'gov.bylaw.zoning',
  noise: 'gov.bylaw.noise',
  animal_control: 'gov.bylaw.animal_control',
  short_term_rental: 'gov.bylaw.short_term_rental',
};

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * Stage 1 — the intake form already ran when the file was created (complaint →
 * file, see NewFile + useIntakeFile). At the intake stage the officer reviews
 * the captured complaint and confirms it to advance into review.
 */
export function IntakeStage({ file }: { file: FileDetail }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // A configured by-law type (property standards, noise, …) moves through its
  // own workflow route; the D&U advance-stage route refuses those files.
  const configured = isConfiguredBylawCategory(file.bylawCategory);
  const advanceStage = useAdvanceStage(file.id);
  const advanceWorkflow = useAdvanceWorkflow(file.id);
  const advance = configured ? advanceWorkflow : advanceStage;
  const complaint = useComplaint(file.complaintId);
  // Administrable lists: both codes are stored, and only these can name them.
  const methods = useIntakeMethods();
  const propertyTypes = usePropertyTypes();

  const onComplete = () => {
    const onSuccess = () => navigate(`/files/${file.id}`);
    if (configured) advanceWorkflow.mutate({ expectedStage: 'intake' }, { onSuccess });
    else advanceStage.mutate(undefined, { onSuccess });
  };

  const dash = '—';
  // Show every linked PID (one PAN → many PIDs), falling back to the scalar.
  const pids = file.parcels?.length
    ? file.parcels.map((p) => p.pid)
    : file.propertyId
      ? [file.propertyId]
      : [];
  const pidsValue = pids.length > 0 ? pids.join('; ') : dash;
  const ownerValue = file.ownerUnknown ? t('gov.file.ownerUnknown') : (file.ownerName ?? dash);
  const priorityValue = t(`gov.priority.${file.priority}`);
  // Both lists are administrable, so the stored code is only ever a key into
  // them. An unknown code falls back to itself rather than to a blank: a value
  // the taxonomy no longer carries is still what this file was classified as.
  const codeLabel = (
    code: string | null | undefined,
    options: { key: string; labelEn: string; labelFr: string }[] | undefined,
  ) => {
    if (!code) return null;
    const option = options?.find((o) => o.key === code);
    return option ? taxonomyLabel(option, i18n.language) : code;
  };
  const method = codeLabel(complaint.data?.intakeMethod, methods.data);
  const methodLabel = method
    ? withSpecify(method, complaint.data?.intakeMethodOther ?? null)
    : null;
  const propertyType = codeLabel(file.propertyType, propertyTypes.data);
  const propertyTypeValue = propertyType ? withSpecify(propertyType, file.propertyTypeOther) : null;
  const sourceValue = complaint.data
    ? t(SOURCE_LABEL_KEY[complaint.data.source])
    : t('gov.intake.sourceUnknown');

  // Don't let an officer confirm intake before the complaint they're meant to
  // review has actually loaded — block while it's pending or errored.
  const complaintReady = !!complaint.data && !complaint.isError;

  // Intake carries more than any other stage — the complaint that opened the
  // file and the whole property tombstone — so its read-only view is the one
  // that most needs to show everything rather than a summary of a summary.
  const record = [
    { label: t('gov.file.source'), value: sourceValue },
    { label: t('gov.file.intakeMethod'), value: methodLabel },
    { label: t('gov.file.complainantName'), value: complaint.data?.complainantName },
    { label: t('gov.file.complainantEmail'), value: complaint.data?.complainantEmail },
    { label: t('gov.file.complainantPhone'), value: complaint.data?.complainantPhone },
    { label: t('gov.file.referringPerson'), value: complaint.data?.referringPerson },
    { label: t('gov.file.internalContext'), value: complaint.data?.internalContext, block: true },
    { label: t('gov.file.address'), value: complaint.data?.subjectAddress || file.address },
    { label: t('gov.file.description'), value: complaint.data?.description, block: true },
    {
      label: t('gov.file.bylawCategory'),
      value: withSpecify(t(BYLAW_LABEL_KEY[file.bylawCategory]), file.bylawCategoryOther),
    },
    { label: t('gov.file.priority'), value: priorityValue },
    { label: t('gov.file.propertyType'), value: propertyTypeValue },
    { label: t('gov.file.pan'), value: file.pan },
    { label: t('gov.file.county'), value: countyLabel(file.county, t) },
    { label: t('gov.file.pids'), value: pids.length > 0 ? pids.join('; ') : null },
    { label: t('gov.file.ownerName'), value: ownerValue },
    {
      label: t('gov.file.ownerType'),
      value: file.ownerType
        ? withSpecify(t(`gov.ownerType.${file.ownerType}`), file.ownerTypeOther)
        : null,
    },
    { label: t('gov.file.mailingAddress'), value: file.mailingAddress },
    { label: t('gov.file.harmonization'), value: file.harmonizationStatus },
  ];

  return (
    <StageLayout
      file={file}
      stageId="intake"
      onComplete={onComplete}
      saving={advance.isPending}
      disabled={!complaintReady}
      completeLabel={t('gov.intake.confirm')}
      record={record}
    >
      {advance.isError && <Alert severity="error">{t('gov.stage.saveError')}</Alert>}

      {/* Review is meaningless without the ability to correct, and both cards
          below are edited from the one screen that created them. */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('gov.intake.summaryHint')}
        </Typography>
        <Button
          size="small"
          startIcon={<Icons.Pencil size={iconSize.sm} />}
          onClick={() => navigate(`/files/${file.id}/edit`)}
          sx={{ flexShrink: 0 }}
        >
          {t('gov.file.edit.action')}
        </Button>
      </Stack>

      <HairlineCard sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('gov.intake.complaint')}
        </Typography>
        {complaint.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : complaint.isError ? (
          <Alert severity="error">{t('gov.intake.complaintLoadError')}</Alert>
        ) : (
          <Stack spacing={1}>
            <DetailRow label={t('gov.file.source')} value={sourceValue} />
            <DetailRow
              label={t('gov.file.complainantName')}
              value={complaint.data?.complainantName || dash}
            />
            <DetailRow
              label={t('gov.file.address')}
              value={complaint.data?.subjectAddress || file.address}
            />
            <Divider />
            <Typography variant="body2" color="text.secondary">
              {t('gov.file.description')}
            </Typography>
            <Typography variant="body2">{complaint.data?.description || dash}</Typography>
          </Stack>
        )}
      </HairlineCard>

      <HairlineCard sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('gov.intake.file')}
        </Typography>
        <Stack spacing={1}>
          <DetailRow
            label={t('gov.file.bylawCategory')}
            value={withSpecify(t(BYLAW_LABEL_KEY[file.bylawCategory]), file.bylawCategoryOther)}
          />
          <DetailRow label={t('gov.file.priority')} value={priorityValue} />
          <DetailRow label={t('gov.file.pan')} value={file.pan ?? dash} />
          <DetailRow label={t('gov.file.county')} value={countyLabel(file.county, t) ?? dash} />
          <DetailRow label={t('gov.file.pids')} value={pidsValue} />
          <DetailRow label={t('gov.file.ownerName')} value={ownerValue} />
          <DetailRow
            label={t('gov.file.ownerType')}
            value={
              file.ownerType
                ? withSpecify(t(`gov.ownerType.${file.ownerType}`), file.ownerTypeOther)
                : dash
            }
          />
        </Stack>
      </HairlineCard>

      {/* What was decided at intake instead of fixed, and why — the same
          reasons the audit trail carries, shown where the PIDs are reviewed. */}
      {(file.intakeConfirmations ?? []).length > 0 && (
        <HairlineCard sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('gov.intakeChecks.fileTitle')}
          </Typography>
          <Stack spacing={1.5} divider={<Divider />}>
            {file.intakeConfirmations.map((c) => (
              <Stack key={`${c.kind}:${c.pid}`} spacing={0.25}>
                <Typography variant="body2">
                  {c.kind === 'pid_not_in_matter'
                    ? t('gov.intakeChecks.notInMatterRow', { pid: c.pid })
                    : t('gov.intakeChecks.outsideRow', {
                        pid: c.pid,
                        place: c.parcelPlace || t('gov.intakeChecks.unknownPlace'),
                        localGovernment: c.localGovernmentName ?? dash,
                      })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {c.reason}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('gov.intakeChecks.confirmedBy', {
                    name: c.confirmedByName ?? dash,
                    date: frenchFirstOfMonth(
                      new Intl.DateTimeFormat(i18n.language?.startsWith('fr') ? 'fr-CA' : 'en-CA', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      }).format(new Date(c.confirmedAt)),
                    ),
                  })}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </HairlineCard>
      )}
    </StageLayout>
  );
}
